import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { Box, FieldRichText, FormField, FormGrid, ModalBody, ModalFoot, ModalHead, } from '@dixa/lib-shared-habla';
import countBy from 'lodash/countBy';
import { useCallback } from 'react';
import { connect } from 'react-redux';
import { Field, FieldArray, Form, formValueSelector, reduxForm, } from 'redux-form';
import { v4 as uuid } from 'uuid';
import { SanitizeHTML } from 'app/utils/htmlSanitizer';
import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { getConfirmButtonVerb } from 'components/powerFlowShared/helpers/getConfirmButtonVerb';
import { extractRichTextContentWithHtmlTags } from 'components/settings/components/sections/dixaMessenger/views/flow/components/shared/utils/extractRichTextContentWithHtmlTags';
import { FieldRepeater, RepeaterDivider } from 'fable/fieldRepeater';
import { useElevioIntegration } from '../../../../elevioIntegrationProvider';
import { useParsedHtmlTextContent } from '../../../../hooks/useParsedHtmlTextContent';
import { useElevioArticleQuickResponse } from '../../../shared/knowledgeBaseLinkPreview/hooks/useElevioRichTextInputQuickResponse';
import { FieldOptions } from './components/fieldOptions/fieldOptions';
import { MessengerMenuPreview } from './components/messengerMenuPreview';
import styles from './menuModal.module.scss';
const getDefaultRowProps = () => ({
    id: uuid(),
    label: '',
    nextNodeId: uuid(),
});
function addLabelFallback(options) {
    return (options === null || options === void 0 ? void 0 : options.map((option) => (Object.assign({ label: '' }, option)))) || [];
}
const validate = (values) => {
    var _a;
    const errors = {};
    if (!values) {
        return errors;
    }
    if (!((_a = values.text) === null || _a === void 0 ? void 0 : _a.trim())) {
        errors.text = 'Message is required';
    }
    const options = addLabelFallback(values.options);
    if (!(options === null || options === void 0 ? void 0 : options.length)) {
        errors._error = 'At least one Option must be added - click on "Add option"';
    }
    else {
        const optionErrors = [];
        options.forEach(({ label }, index) => {
            if (!(label === null || label === void 0 ? void 0 : label.trim())) {
                optionErrors[index] = { label: 'Required' };
            }
        });
        const counts = countBy(options, ({ label }) => label);
        options.forEach(({ label }, index) => {
            if (counts[label] > 1 && !!(label === null || label === void 0 ? void 0 : label.trim())) {
                optionErrors[index] = {
                    label: "You can't have duplicate options",
                };
            }
        });
        if (optionErrors.length) {
            errors.options = optionErrors;
        }
    }
    return errors;
};
const MenuModalImpl = (props) => {
    var _a;
    const { handleSubmit, node, nodeLabel, replaceNode, triggerClose, destroy, text, options, dirty: isChanged, } = props;
    const { elevioIntegration } = useElevioIntegration();
    const hasElevioIntegration = Boolean(elevioIntegration === null || elevioIntegration === void 0 ? void 0 : elevioIntegration.companyId);
    const handleSaveClick = useCallback((values) => {
        const nodeOptions = Object.assign(Object.assign({}, node), { badges: [], childIds: [], icon: '', isAction: false, isEditable: true, isRemovable: true, nodeType: FlowNodeTypes.MESSENGER_MENU, overlay: [], payload: {
                text: values.text,
                options: addLabelFallback(values.options),
            }, text: '' });
        replaceNode(nodeOptions);
        triggerClose();
    }, [node, replaceNode, triggerClose]);
    const closeModal = useCallback(() => {
        destroy();
        triggerClose();
    }, [destroy, triggerClose]);
    const { quickResponseApi, quickResponseRenderer } = useElevioArticleQuickResponse({
        search: useParsedHtmlTextContent(text),
    });
    return (_jsxs(Box, Object.assign({ width: "800px" }, { children: [_jsxs(ModalHead, Object.assign({ title: nodeLabel, onClose: closeModal }, { children: ["The \"", nodeLabel, "\" action allows you to automate quick inquiries and gain more context before handing complex ones over to your team."] })), _jsxs(Form, Object.assign({ onSubmit: handleSubmit(handleSaveClick) }, { children: [_jsx(ModalBody, { children: _jsxs(FormGrid, { children: [_jsxs(FormField, Object.assign({ span: 3 }, { children: [_jsx(Field, { component: FieldRichText, name: "text", label: "Your message", className: styles.editor, quickResponseIcon: "elevio", quickResponsesApi: hasElevioIntegration ? quickResponseApi : undefined, renderQuickResponse: hasElevioIntegration ? quickResponseRenderer : undefined, sanitize: SanitizeHTML.sanitizerForChannel('messenger'), languages: (_a = elevioIntegration === null || elevioIntegration === void 0 ? void 0 : elevioIntegration.availableLocales) !== null && _a !== void 0 ? _a : [], editorContentExtractor: extractRichTextContentWithHtmlTags }), _jsx(FieldArray, { buttonProps: { placement: 'outside', label: 'Add option' }, component: FieldRepeater, defaultRowProps: getDefaultRowProps, divider: _jsx(RepeaterDivider, {}), rowProps: {}, name: "options", rowComponent: FieldOptions, title: "Options" })] })), _jsx(FormField, Object.assign({ span: 3 }, { children: _jsx(MessengerMenuPreview, { text: text, options: options }) }))] }) }), _jsx(ModalFoot, { confirmOnClick: handleSubmit(handleSaveClick), confirmName: getConfirmButtonVerb(node), confirmProps: { disabled: !isChanged }, cancelOnClick: closeModal })] }))] })));
};
const formName = 'messengerMenuModal';
const mapStateToProps = (state, ownProps) => {
    const { payload } = ownProps.node;
    const selector = formValueSelector(formName);
    return {
        initialValues: {
            text: (payload === null || payload === void 0 ? void 0 : payload.text) || '',
            options: (payload === null || payload === void 0 ? void 0 : payload.options) || [],
        },
        text: selector(state, 'text') || '',
        options: selector(state, 'options') || [],
    };
};
export const MenuModal = connect(mapStateToProps)(reduxForm({
    enableReinitialize: true,
    form: formName,
    touchOnBlur: false,
    validate,
})(MenuModalImpl));
